import { Grid } from "@mui/material";
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import PortalShell from "../commonComponents/appShell/portalShell";
import EmptyState from "../reusableComponents/emptyState/emptyState";
import { clientUserPicksContext } from "../../models/program";
import { RoleIndex } from "../../models/role";
import { UserType } from "../../models/userType";
import { getLastUpdated } from "../../store/thunks/portalManagementThunk";
import { logoutUser } from "../../store/thunks/authenticationThunk";
import { logout } from "../../store/features/authentication/authenticationSlice";
import { systemManagementReset } from "../../store/features/systemManagement/systemManagementSlice";
import {
  portalManagementReset,
  setSelectedClientOther,
  setSelectedSalesRep,
} from "../../store/features/portalManagement/portalManagementSlice";
import {
  programForUserType,
  userTypeIndexForName,
  viewForProgram,
} from "./dashboardViewModel";

/** /dashboard: establish a context, then show that program's view. */
const DashboardView = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { selectedClient, selectedProgram, selectedUserType, selectedSalesRep, contextCommitted } =
    useAppSelector((s: any) => s.portalManagement);
  const { user, loggedIn } = useAppSelector((s: any) => s.authentication);

  const isClientUser = user?.role_index === RoleIndex.ClientUser;
  const clientUserProgram = isClientUser
    ? programForUserType(user?.client_type_name)
    : undefined;

  // Committed, not merely selected: legacy writes each pick straight to the
  // store. `??` covers snapshots persisted before the field existed. Client
  // users always have a context — nothing for them to choose.
  // A Manager picks a context like anyone else: the chooser filters user types
  // by program, so they can adopt Sales_Rep and review an individual rep. Every
  // other client user has nothing to choose and lands directly.
  // Not gated on user.client_index: the chooser gets the client — index and all —
  // from getUserClients, so it works whatever the login payload happens to carry.
  // Gating on it meant a Manager whose session predated that field, or whose
  // backend had not shipped it yet, never saw the chooser at all.
  const picksContext = isClientUser && clientUserPicksContext(user?.client_type_name);
  const hasContext =
    (isClientUser && !picksContext) ||
    (contextCommitted ?? Boolean(selectedClient && selectedProgram));

  // Whether the seeding effect below has landed. A sales rep additionally needs
  // their rep in the store before their view will fetch anything.
  //
  // Keyed on client_id, NOT client_index: the login payload has no client_index
  // (auth_service.get_client_context returns client_id / client_name only), so
  // comparing it compared undefined with undefined — true on the very first
  // render, which reported "ready" with no client at all and rendered an empty
  // dashboard.
  const clientContextReady =
    !isClientUser ||
    (Boolean(selectedClient) &&
      selectedClient.customer_id === user?.client_id &&
      (user?.client_type_name !== UserType.Name.SalesRep ||
        selectedSalesRep?.SALESMAN === user?.sale_rep));

  const programIndex = selectedProgram?.program_index ?? clientUserProgram;
  // Not seeded into the store: trainingView sends selectedUserType?.name as
  // account_type, so writing it would change a request payload.
  const userTypeIndex =
    selectedUserType?.client_user_type_index ??
    (isClientUser ? userTypeIndexForName(user?.client_type_name) : undefined);

  useEffect(() => {
    if (!loggedIn || !user) navigate("/login");
  }, [loggedIn, user, navigate]);

  // Seed the context a client user cannot choose.
  useEffect(() => {
    if (!isClientUser || !user) return;

    // Same reason: client_id is the field that actually arrives. The old
    // portalView dispatched this unconditionally, so a guard on a missing field
    // meant it never dispatched at all.
    if (!selectedClient || selectedClient.customer_id !== user.client_id) {
      dispatch(
        setSelectedClientOther({
          client_index: user.client_index,
          customer_id: user.client_id,
          customer_name: user.client_name,
        })
      );
    }

    // The program is NOT written to the store. setSelectedProgram clears the
    // sales rep, and seeding it here wiped the rep the line below had just
    // confirmed — SalesRepView then rendered with an empty series and MUI X
    // threw into the ErrorBoundary. clientUserProgram is read locally instead,
    // which also leaves legacy chrome deriving from archetype as it did before.
    if (
      user.client_type_name === UserType.Name.SalesRep &&
      selectedSalesRep?.SALESMAN !== user.sale_rep
    ) {
      dispatch(setSelectedSalesRep({ SALESMAN: user.sale_rep }));
    }
  }, [
    dispatch,
    isClientUser,
    user,
    selectedClient?.customer_id,
    selectedSalesRep?.SALESMAN,
  ]);

  // A rejected freshness check means the session is gone.
  //
  // Gated on a committed, rendered context: ungated, this fired once per
  // client an admin clicked through in the legacy chooser (legacy writes each
  // pick straight to the store) and while the chooser was still open over a
  // stale persisted client. The old app only ran it for the mounted dashboard.
  // Direct-landing sales-rep/training users DO keep the check — the original
  // never ran it for them; keeping it gives their legacy header a real
  // "Last Update" value and catches expired sessions. Both differences are on
  // the legacy README's Known-deviations list.
  useEffect(() => {
    if (!hasContext || !clientContextReady) return;
    if (!selectedClient?.customer_id) return;
    dispatch(getLastUpdated({ payload: selectedClient.customer_id })).then((result: any) => {
      if (result?.error?.message === "Rejected") {
        dispatch(logoutUser({ user_index: user?.client_user_index }));
        dispatch(portalManagementReset());
        dispatch(systemManagementReset());
        dispatch(logout());
        navigate("/login");
      }
    });
  }, [dispatch, navigate, user, selectedClient?.customer_id, hasContext, clientContextReady]);

  // A client user with no client resolves to client_id 0, and every figure on
  // the dashboard then reads 0.0% — which looks like broken software rather
  // than an unfinished account. Say which it is. This must come BEFORE the
  // chooser branch: an unassigned Manager picksContext, but has no client to
  // choose from, so the chooser is a dead end for them — sending them there
  // (bf2d864) replaced this message with two dropdowns that never populate.
  if (isClientUser && !user?.client_id) {
    return (
      <PortalShell>
        <Grid container sx={{ p: 3 }}>
          <Grid item xs={12}>
            <EmptyState
              kind="empty"
              title="No account assigned"
              body="This login is not attached to a client yet, so there is no data to show. An administrator can assign one from System Management."
            />
          </Grid>
        </Grid>
      </PortalShell>
    );
  }

  // No context: shell in `chooser` mode, DashboardChooser open over it.
  if (!hasContext) {
    return <PortalShell chooser>{null}</PortalShell>;
  }

  // A client user's context is seeded by the effect above, which runs after the
  // first render. Mounting the view before it lands means SalesRepView fetches
  // with a null rep and MUI X throws on the empty series.
  if (!clientContextReady) {
    return <PortalShell>{null}</PortalShell>;
  }

  const ViewComponent = viewForProgram(programIndex, userTypeIndex);

  return (
    <PortalShell>
      <Grid container>{ViewComponent ? <ViewComponent /> : null}</Grid>
    </PortalShell>
  );
};

export default DashboardView;
